import { tokenize, Token, Tokenizer } from './tokenizer';
import {
  AST,
  BlockNode,
  InlineNode,
  DocumentNode,
  HeadingNode,
  ParagraphNode,
  BlockquoteNode,
  ListNode,
  ListItemNode,
  CodeBlockNode,
  HrNode,
  TableNode,
  ModuleBlockNode,
  AlignmentBlockNode,
  FloatBlockNode,
  TextNode,
  BoldNode,
  ItalicNode,
  CodeNode,
  LinkNode,
  ModuleInlineNode,
  LatexNode,
  FootnoteNode,
  EndnoteNode,
  CitationNode,
} from './nodes';
import { moduleRegistry } from '../../modules/registry';

export class MarkdownParser {
  private tokens: Token[] = [];
  private pos = 0;

  parse(source: string): AST {
    this.tokens = tokenize(source);
    this.pos = 0;

    const children: BlockNode[] = [];

    while (this.pos < this.tokens.length) {
      const token = this.tokens[this.pos];

      if (token.type === 'eof') break;

      // Skip newline tokens
      if (token.type === 'newline') {
        this.pos++;
        continue;
      }

      const node = this.parseBlock();
      if (node) {
        children.push(node);
      }
    }

    const document: DocumentNode = {
      type: 'document',
      children,
    };

    return { document };
  }

  private parseBlock(): BlockNode | null {
    const token = this.peek();

    if (!token) return null;

    switch (token.type) {
      case 'heading':
        return this.parseHeading();
      case 'blockquote':
        return this.parseBlockquote();
      case 'listItem':
        return this.parseList();
      case 'codeBlock':
        return this.parseCodeBlock();
      case 'hr':
        this.advance();
        return { type: 'hr' };
      case 'table':
      case 'tableRow':
        return this.parseTable();
      case 'openTag':
      case 'selfClosingTag':
        return this.parseModuleBlock();
      case 'alignment':
        return this.parseAlignmentBlock();
      case 'float':
        return this.parseFloatBlock();
      case 'latex':
        return this.parseParagraph();
      default:
        return this.parseParagraph();
    }
  }

  private parseHeading(): HeadingNode {
    const token = this.expect('heading');
    this.advance();

    return {
      type: 'heading',
      level: token.level!,
      text: this.parseInline(token.content || ''),
    };
  }

  private parseParagraph(): ParagraphNode {
    const children: InlineNode[] = [];

    while (this.pos < this.tokens.length) {
      const token = this.peek();

      if (!token) break;
      if (token.type === 'eof') break;
      if (token.type === 'newline') {
        this.advance();
        if (this.peek()?.type === 'newline') {
          break;
        }
        // Add space for line break within paragraph
        children.push({ type: 'text', content: ' ' });
        continue;
      }
      if (token.type === 'heading') break;
      if (token.type === 'blockquote') break;
      if (token.type === 'listItem') break;
      if (token.type === 'codeBlock') break;
      if (token.type === 'hr') break;
      if (token.type === 'table') break;
      if (token.type === 'tableRow') break;
      if (token.type === 'openTag' || token.type === 'selfClosingTag') break;

      if (token.type === 'text') {
        this.advance();
        const inlineNodes = this.parseInline(token.content || '');
        children.push(...inlineNodes);
      } else if (token.type === 'latex') {
        this.advance();
        children.push({ type: 'latex', content: token.content || '' });
      } else {
        // Skip unknown tokens
        this.advance();
      }
    }

    // Remove leading/trailing whitespace text nodes
    const trimmedChildren = children.filter((child, i) => {
      if (child.type === 'text' && child.content.trim() === '') {
        // Keep if it's the only child or not at edges
        return children.length === 1 || (i > 0 && i < children.length - 1);
      }
      return true;
    });

    return {
      type: 'paragraph',
      children: trimmedChildren.length > 0 ? trimmedChildren : [{ type: 'text', content: '' }],
    };
  }

  private parseBlockquote(): BlockquoteNode {
    this.expect('blockquote');
    const content = this.advance()?.content || '';

    return {
      type: 'blockquote',
      children: [
        {
          type: 'paragraph',
          children: this.parseInline(content),
        },
      ],
    };
  }

  private parseList(): ListNode {
    const first = this.peek();
    const ordered = first?.ordered || false;
    const items: ListItemNode[] = [];
    const indentStack: ListItemNode[] = [];
    const indentToLevel = new Map<number, number>();
    let currentLevel = 0;

    const getLevel = (indent: number): number => {
      if (!indentToLevel.has(indent)) {
        indentToLevel.set(indent, indentToLevel.size + 1);
      }
      return indentToLevel.get(indent)!;
    };

    while (this.pos < this.tokens.length) {
      let token = this.peek();

      while (token?.type === 'newline') {
        this.advance();
        token = this.peek();
      }

      if (token?.type !== 'listItem') break;

      const itemIndent = token.indent || 0;
      const itemLevel = getLevel(itemIndent);
      const itemOrdered = token.ordered;

      if (itemOrdered !== ordered && itemLevel === 1) {
        break;
      }

      this.advance();

      if (itemLevel > currentLevel) {
        const newItem: ListItemNode = {
          type: 'listItem',
          indent: itemIndent,
          children: [
            {
              type: 'paragraph',
              children: this.parseInline(token.content || ''),
            },
          ],
        };

        if (indentStack.length > 0) {
          const parent = indentStack[indentStack.length - 1];
          if (!parent.children) parent.children = [];
          const lastChild =
            parent.children.length > 0 ? parent.children[parent.children.length - 1] : null;
          if (!lastChild || lastChild.type === 'paragraph') {
            parent.children.push({
              type: 'list',
              ordered: itemOrdered,
              children: [newItem],
            } as ListNode);
          } else if (lastChild.type === 'list' && (lastChild).ordered === itemOrdered) {
            (lastChild).children.push(newItem);
          } else {
            parent.children.push({
              type: 'list',
              ordered: itemOrdered,
              children: [newItem],
            } as ListNode);
          }
        } else {
          items.push(newItem);
        }
        indentStack.push(newItem);
        currentLevel = itemLevel;
      } else if (itemLevel < currentLevel) {
        while (indentStack.length > 0) {
          const topLevel = getLevel(indentStack[indentStack.length - 1].indent || 0);
          if (topLevel >= itemLevel) {
            indentStack.pop();
          } else {
            break;
          }
        }

        const newItem: ListItemNode = {
          type: 'listItem',
          indent: itemIndent,
          children: [
            {
              type: 'paragraph',
              children: this.parseInline(token.content || ''),
            },
          ],
        };

        if (indentStack.length > 0) {
          const parent = indentStack[indentStack.length - 1];
          if (!parent.children) parent.children = [];
          const lastChild =
            parent.children.length > 0 ? parent.children[parent.children.length - 1] : null;
          if (
            lastChild?.type === 'list' &&
            (lastChild).ordered === itemOrdered
          ) {
            (lastChild).children.push(newItem);
          } else {
            parent.children.push({
              type: 'list',
              ordered: itemOrdered,
              children: [newItem],
            } as ListNode);
          }
        } else {
          items.push(newItem);
        }
        indentStack.push(newItem);
        currentLevel = itemLevel;
      } else {
        while (indentStack.length > 0) {
          const topLevel = getLevel(indentStack[indentStack.length - 1].indent || 0);
          if (topLevel >= itemLevel) {
            indentStack.pop();
          } else {
            break;
          }
        }

        const newItem: ListItemNode = {
          type: 'listItem',
          indent: itemIndent,
          children: [
            {
              type: 'paragraph',
              children: this.parseInline(token.content || ''),
            },
          ],
        };

        if (indentStack.length > 0) {
          const parent = indentStack[indentStack.length - 1];
          if (!parent.children) parent.children = [];
          const lastChild =
            parent.children.length > 0 ? parent.children[parent.children.length - 1] : null;
          if (
            lastChild?.type === 'list' &&
            (lastChild).ordered === itemOrdered
          ) {
            (lastChild).children.push(newItem);
          } else {
            parent.children.push({
              type: 'list',
              ordered: itemOrdered,
              children: [newItem],
            } as ListNode);
          }
        } else {
          items.push(newItem);
        }
        indentStack.push(newItem);
        currentLevel = itemLevel;
      }
    }

    return {
      type: 'list',
      ordered,
      children: items,
    };
  }

  private parseCodeBlock(): CodeBlockNode {
    const token = this.expect('codeBlock');
    this.advance();

    return {
      type: 'codeBlock',
      language: token.language,
      content: token.content || '',
    };
  }

  private parseTable(): TableNode {
    const token = this.expect('tableRow');
    this.advance();

    const headers = (token.content || '')
      .split('|')
      .map((h) => h.trim())
      .filter((h) => h);
    const headerCells: InlineNode[][] = headers.map((h) => this.parseInlineContent(h));

    const rows: string[][] = [];
    const cells: InlineNode[][][] = [];

    // Skip separator row if present
    let hasSeparator = false;
    if (this.pos < this.tokens.length) {
      let next = this.peek();

      // Skip newlines
      while (next?.type === 'newline') {
        this.advance();
        next = this.peek();
      }

      if (next?.type === 'tableRow') {
        const separatorContent = next.content || '';
        if (
          separatorContent.includes('---') ||
          separatorContent.includes(':--') ||
          separatorContent.includes(':-')
        ) {
          // This is a separator row, skip it
          this.advance();
          hasSeparator = true;
        }
      }
    }

    // Read data rows
    while (this.pos < this.tokens.length) {
      let next = this.peek();

      // Skip newlines between table rows
      while (next?.type === 'newline') {
        this.advance();
        next = this.peek();
      }

      if (!next || (next.type !== 'tableRow' && next.type !== 'table')) break;

      this.advance();
      if (next.type === 'tableRow') {
        const row = (next.content || '')
          .split('|')
          .map((c) => c.trim())
          .filter((c) => c);
        if (row.length > 0) {
          rows.push(row);
          // Also parse inline content for each cell
          cells.push(row.map((c) => this.parseInlineContent(c)));
        }
      }
    }

    return {
      type: 'table',
      headers,
      rows,
      headerCells,
      cells,
    };
  }

  private parseAlignmentBlock(): AlignmentBlockNode {
    const token = this.expect('alignment');
    this.advance();

    const align: 'left' | 'center' | 'right' | 'justify' = token.align || 'left';
    const content = token.content || '';

    // Parse the content inside the alignment block
    const parser = new MarkdownParser();
    const ast = parser.parse(content);
    const {children} = ast.document;

    return {
      type: 'alignment',
      align,
      children,
    };
  }

  private parseFloatBlock(): FloatBlockNode {
    const token = this.expect('float');
    this.advance();

    const align: 'left' | 'right' = token.align === 'right' ? 'right' : 'left';
    const {width} = token;
    const content = token.content || '';

    // Parse the content inside the float block
    const parser = new MarkdownParser();
    const ast = parser.parse(content);

    // Get all children as the floated content (supports multiple paragraphs, etc.)
    const blockContent: BlockNode[] =
      ast.document.children.length > 0
        ? ast.document.children
        : [{ type: 'paragraph', children: [] } as ParagraphNode];

    return {
      type: 'float',
      align,
      width,
      content: blockContent,
    };
  }

  private parseInlineContent(text: string): InlineNode[] {
    const parser = new InlineParser(text);
    return parser.parse();
  }

  private parseModuleBlock(): ModuleBlockNode {
    const token = this.peek();

    if (!token) {
      throw new Error('Unexpected end of input');
    }

    if (token.type === 'openTag' || token.type === 'selfClosingTag') {
      this.advance();

      return {
        type: 'module',
        moduleName: token.name!,
        content: token.content || '',
        attrs: token.attrs || {},
      };
    }

    throw new Error(`Expected module tag, got ${token.type}`);
  }

  private parseInline(content: string): InlineNode[] {
    const parser = new InlineParser(content);
    return parser.parse();
  }

  private parseInlineToken(): InlineNode | null {
    const token = this.peek();

    if (!token) return null;

    switch (token.type) {
      case 'text':
        this.advance();
        // Parse inline formatting within text
        const inlineNodes = this.parseInline(token.content || '');
        if (inlineNodes.length === 0) {
          return { type: 'text', content: '' } as InlineNode;
        } else if (inlineNodes.length === 1) {
          return inlineNodes[0];
        } else {
          // If multiple nodes, we need to handle this case
          // For now, return a text node with the original content
          return { type: 'text', content: token.content || '' } as InlineNode;
        }

      case 'bold':
        this.advance();
        return { type: 'bold', children: this.parseInline(token.content || '') };

      case 'italic':
        this.advance();
        return { type: 'italic', children: this.parseInline(token.content || '') };

      case 'code':
        this.advance();
        return { type: 'code', content: token.content || '' };

      case 'link':
        this.advance();
        return {
          type: 'link',
          href: token.href || '',
          title: token.title,
          children: this.parseInline(token.content || ''),
        };

      case 'latex':
        this.advance();
        return { type: 'latex', content: token.content || '' };

      case 'openTag':
      case 'selfClosingTag':
        this.advance();
        return {
          type: 'module',
          moduleName: token.name!,
          content: token.content || '',
          attrs: token.attrs || {},
        };

      default:
        return null;
    }
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private advance(): Token | undefined {
    return this.tokens[this.pos++];
  }

  private expect(type: string): Token {
    const token = this.peek();
    if (!token) {
      throw new Error(`Expected ${type}, got end of input`);
    }
    if (token.type !== type) {
      throw new Error(`Expected ${type}, got ${token.type}`);
    }
    return token;
  }
}

class InlineParser {
  private pos = 0;
  private source: string;

  constructor(source: string) {
    this.source = source;
  }

  parse(): InlineNode[] {
    const nodes: InlineNode[] = [];
    let iterations = 0;
    const maxIterations = this.source.length * 2;

    while (this.pos < this.source.length) {
      iterations++;
      if (iterations > maxIterations) {
        throw new Error(
          `Inline parser stuck at position ${this.pos} in source: "${this.source.substring(this.pos, Math.min(this.pos + 20, this.source.length))}..."`
        );
      }

      const startPos = this.pos;
      const node = this.parseOne();
      if (node) {
        nodes.push(node);
      }

      if (this.pos === startPos) {
        this.pos++;
      }
    }

    return nodes;
  }

  private parseOne(): InlineNode | null {
    if (this.pos >= this.source.length) return null;

    const char = this.source[this.pos];

    if (char === '\\') {
      return this.parseEscaped();
    }

    if (char === '*' || char === '_') {
      return this.parseEmphasis();
    }

    if (char === '`') {
      return this.parseCode();
    }

    if (char === '[') {
      const start = this.pos;
      const result = this.parseLink();
      if (result === null) {
        this.pos = start;
        this.pos++;
        return { type: 'text', content: char };
      }
      return result;
    }

    if (char === '<') {
      const start = this.pos;
      const result = this.parseModule();
      if (result === null) {
        this.pos = start;
        this.pos++;
        return { type: 'text', content: char };
      }
      return result;
    }

    if (char === '$') {
      if (this.source[this.pos + 1] === '$') {
        return this.parseLatex(); // $$...$$ display math
      }
      return this.parseSingleLatex(); // $...$ inline math
    }

    return this.parseText();
  }

  private parseText(): TextNode {
    let content = '';

    while (this.pos < this.source.length) {
      const char = this.source[this.pos];
      if (['*', '_', '`', '[', '<', '$', '\\', ']'].includes(char)) break;
      content += char;
      this.pos++;
    }

    return { type: 'text', content };
  }

  private parseEscaped(): InlineNode {
    this.pos++;
    const char = this.source[this.pos];
    this.pos++;
    return { type: 'text', content: char || '' };
  }

  private parseEmphasis(): InlineNode | null {
    const start = this.pos;
    const char = this.source[this.pos];
    this.pos++;

    let count = 1;
    while (this.pos < this.source.length && this.source[this.pos] === char) {
      count++;
      this.pos++;
    }

    if (count === 1) {
      const content = this.readUntil(char);
      if (this.pos < this.source.length && this.source[this.pos] === char) {
        this.pos++;

        if (char === '*' || char === '_') {
          return { type: 'italic', children: [{ type: 'text', content }] };
        }
      }
    }

    if (count >= 2) {
      const content = this.readUntil(char.repeat(count));
      if (
        this.pos >= this.source.length ||
        this.source.slice(this.pos, this.pos + count) === char.repeat(count)
      ) {
        this.pos += count;

        if (char === '*' || char === '_') {
          return { type: 'bold', children: [{ type: 'text', content }] };
        }
      }
    }

    this.pos = start;
    return { type: 'text', content: char };
  }

  private parseCode(): InlineNode {
    this.pos++;
    const content = this.readUntil('`');
    if (this.pos < this.source.length) {
      this.pos++;
    }
    return { type: 'code', content };
  }

  private parseLink(): InlineNode | null {
    if (this.source[this.pos] !== '[') return null;
    this.pos++;

    // Footnote: [^: content]
    if (this.source[this.pos] === '^' && this.source[this.pos + 1] === ':') {
      this.pos += 2;
      if (this.source[this.pos] === ' ') this.pos++;
      const content = this.readUntil(']');
      if (this.pos < this.source.length) this.pos++;
      return { type: 'footnote', content: content.trim() } as FootnoteNode;
    }

    // Endnote: [~: content]
    if (this.source[this.pos] === '~' && this.source[this.pos + 1] === ':') {
      this.pos += 2;
      if (this.source[this.pos] === ' ') this.pos++;
      const content = this.readUntil(']');
      if (this.pos < this.source.length) this.pos++;
      return { type: 'endnote', content: content.trim() } as EndnoteNode;
    }

    // Citation: [@id: content]
    if (this.source[this.pos] === '@') {
      this.pos++;
      let id = '';
      while (
        this.pos < this.source.length &&
        this.source[this.pos] !== ':' &&
        this.source[this.pos] !== ']'
      ) {
        id += this.source[this.pos++];
      }
      if (this.pos < this.source.length && this.source[this.pos] === ':') {
        this.pos++;
        if (this.source[this.pos] === ' ') this.pos++;
        const content = this.readUntil(']');
        if (this.pos < this.source.length) this.pos++;
        return { type: 'citation', id: id.trim(), content: content.trim() } as CitationNode;
      }
      // Not a valid citation — return as text
      const rest = this.readUntil(']');
      if (this.pos < this.source.length) this.pos++;
      return { type: 'text', content: `[@${id}${rest}]` };
    }

    const text = this.readUntil(']');
    if (this.pos >= this.source.length || this.source[this.pos] !== ']') {
      this.pos--;
      return null;
    }
    this.pos++;

    if (this.source[this.pos] !== '(') {
      return { type: 'text', content: `[${text}]` };
    }
    this.pos++;

    const href = this.readUntil(')');
    if (this.pos < this.source.length) {
      this.pos++;
    }

    return { type: 'link', href, children: [{ type: 'text', content: text }] };
  }

  private parseModule(): InlineNode | null {
    if (this.source[this.pos] !== '<') return null;

    const end = this.source.indexOf('>', this.pos);
    if (end === -1) return null;

    const tag = this.source.slice(this.pos + 1, end);
    const match = /^(\w+)(.*)$/.exec(tag);

    if (!match) return null;

    const name = match[1];
    const attrsStr = match[2];

    const attrs: Record<string, string> = {};
    const attrRegex = /(\w+)=(?:"([^"]*)"|'([^']*)')/g;
    let attrMatch;

    while ((attrMatch = attrRegex.exec(attrsStr)) !== null) {
      attrs[attrMatch[1]] = attrMatch[2] || attrMatch[3] || '';
    }

    let content = '';
    const closePos = this.findMatchingCloseTag(name, end + 1);

    if (closePos !== -1) {
      content = this.source.slice(end + 1, closePos);
      this.pos = closePos + name.length + 3;
    } else {
      this.pos = end + 1;
    }

    const module = moduleRegistry.get(name);
    if (module) {
      return { type: 'module', moduleName: name, content, attrs };
    }

    return { type: 'text', content: `<${tag}>` };
  }

  private findMatchingCloseTag(tagName: string, startPos: number): number {
    let depth = 1;
    let pos = startPos;

    while (pos < this.source.length) {
      if (this.source[pos] === '<') {
        if (this.source.slice(pos, pos + 4) === '<!--') {
          const commentEnd = this.source.indexOf('-->', pos + 4);
          if (commentEnd === -1) return -1;
          pos = commentEnd + 3;
          continue;
        }

        const isClose = this.source[pos + 1] === '/';
        if (isClose) {
          const closeCheck = this.source.slice(pos + 2, pos + 2 + tagName.length);
          if (closeCheck === tagName) {
            depth--;
            if (depth === 0) {
              return pos;
            }
            pos += tagName.length + 3;
            continue;
          }
        }

        const isSelfClosing = this.source.slice(pos, pos + 2).endsWith('/>');
        if (!isSelfClosing && this.source[pos + 1] !== '!') {
          let tagEnd = pos + 1;
          while (tagEnd < this.source.length && /[\w\-:]/.test(this.source[tagEnd])) {
            tagEnd++;
          }
          const innerTagName = this.source.slice(pos + 1, tagEnd);
          if (innerTagName === tagName) {
            depth++;
          }
        }
      }
      pos++;
    }

    return -1;
  }

  private parseLatex(): InlineNode | null {
    // Only $$...$$ is supported for display math
    if (this.source[this.pos] !== '$' || this.source[this.pos + 1] !== '$') return null;

    this.pos += 2;
    const content = this.readUntil('$$');
    if (this.pos < this.source.length) {
      this.pos += 2;
    }
    return { type: 'latex', content, display: false };
  }

  // $...$ inline math — reads content raw so backslashes are preserved for LaTeX
  private parseSingleLatex(): InlineNode {
    this.pos++; // consume opening $
    let content = '';
    while (this.pos < this.source.length) {
      const char = this.source[this.pos];
      if (char === '$') {
        this.pos++; // consume closing $
        return { type: 'latex', content, display: false } as LatexNode;
      }
      if (char === '\n' || char === '\r') break; // no multi-line inline math
      content += char;
      this.pos++;
    }
    // No closing $ found — treat as literal text
    return { type: 'text', content: `$${  content}` };
  }

  private readUntil(terminator: string): string {
    const start = this.pos;
    while (this.pos < this.source.length) {
      if (this.source.slice(this.pos, this.pos + terminator.length) === terminator) {
        break;
      }
      this.pos++;
    }
    return this.source.slice(start, this.pos);
  }
}

export function parseMarkdown(source: string): AST {
  const parser = new MarkdownParser();
  return parser.parse(source);
}
